import { useLanguage, t } from "../../../i18n";
import React, { useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { HiOutlineFlag } from "react-icons/hi2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../../config/routes";
import reportRoomListing from "../../../features/listing/services/reportRoomListing";
import { useAuth } from "../../../features/auth/context/AuthContext";
import "./ReportListing.css";
const REPORT_REASON_OPTIONS = [{
  value: "spam",
  label: "Spam or misleading listing"
}, {
  value: "fake_listing",
  label: "Fake or non-genuine listing"
}, {
  value: "duplicate",
  label: "Duplicate listing"
}, {
  value: "wrong_information",
  label: "Wrong or inaccurate information"
}, {
  value: "inappropriate_content",
  label: "Inappropriate content or images"
}, {
  value: "scam_or_fraud",
  label: "Possible scam or fraud"
}, {
  value: "harassment_or_discrimination",
  label: "Harassment or discrimination"
}, {
  value: "prohibited_listing",
  label: "Prohibited or policy-violating listing"
}, {
  value: "other",
  label: "Other"
}];
export default function ReportListing({
  listingId,
  isOwner = false
}) {
  useLanguage();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isProfileComplete = user?.profile_status === "complete";
  const isNafathVerified = user?.nafath_status === "verified";
  const canReport = useMemo(() => {
    return Boolean(listingId) && !isOwner;
  }, [listingId, isOwner]);
  const resetModalState = () => {
    setReason("");
    setDescription("");
    setReasonError("");
    setIsSubmitting(false);
  };
  const handleClose = () => {
    if (isSubmitting) return;
    setShowModal(false);
    resetModalState();
  };
  const handleOpen = () => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }
    if (!isProfileComplete) {
      toast.info(t("Please complete your profile before reporting a listing."));
      navigate(ROUTES.COMPLETE_PROFILE);
      return;
    }
    if (!isNafathVerified) {
      toast.info(t("Please complete Nafath verification before reporting a listing."));
      navigate(ROUTES.NAFATH_VERIFICATION);
      return;
    }
    if (!canReport) return;
    setShowModal(true);
  };
  const handleSubmit = async event => {
    event.preventDefault();
    if (!reason) {
      setReasonError("Please select a reason for reporting this listing.");
      return;
    }
    try {
      setIsSubmitting(true);
      setReasonError("");
      await reportRoomListing(listingId, {
        reason,
        description: description.trim()
      });
      toast.success(t("Report submitted successfully"));
      setShowModal(false);
      resetModalState();
    } catch (error) {
      toast.error(t(error?.response?.data?.message || error?.message || "Failed to submit report."));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!canReport) {
    return null;
  }
  return <>
      <div className="bb-report-listing">
        <div className="bb-report-listing__content">
          <h6 className="bb-report-listing__title">{t("Notice something wrong?")}</h6>
          <p className="bb-report-listing__text">{t("Help keep BaytBuddy safe by reporting spam, fake, abusive, or inappropriate listings.")}


          </p>
        </div>

        <Button variant="link" type="button" className="bb-report-listing__trigger" onClick={handleOpen}>

          <HiOutlineFlag className="me-2" />{t("Report this listing")}

        </Button>
      </div>

      <Modal show={showModal} onHide={handleClose} centered backdrop={isSubmitting ? "static" : true} keyboard={!isSubmitting} dialogClassName="bb-report-listing-modal">

        <Modal.Header closeLabel={t("Close")} closeButton={!isSubmitting}>
          <Modal.Title>{t("Report Listing")}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t("Reason ")}
                <span className="bb-required-mark">*</span>
              </Form.Label>

              <Form.Select value={reason} onChange={e => {
              setReason(e.target.value);
              if (e.target.value) {
                setReasonError("");
              }
            }} isInvalid={!!reasonError} disabled={isSubmitting}>

                <option value="">{t("Select report reason")}</option>
                {REPORT_REASON_OPTIONS.map(item => <option key={item.value} value={item.value}>
                    {t(item.label)}
                  </option>)}
              </Form.Select>

              <Form.Control.Feedback type="invalid">
                {t(reasonError)}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>{t("Description (Optional)")}</Form.Label>
              <Form.Control as="textarea" rows={4} maxLength={500} placeholder={t("Add more details to help us understand the issue.")} value={description} onChange={e => setDescription(e.target.value)} disabled={isSubmitting} />

              <div className="bb-report-listing__helper">
                {t(description.length)}{t("/500 characters")}
              </div>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" type="button" onClick={handleClose} disabled={isSubmitting}>{t("Cancel")}


            </Button>

            <Button type="submit" className="bb-report-listing__submit-btn" disabled={isSubmitting}>

              {isSubmitting ? <>
                  <Spinner size="sm" animation="border" className="me-2" />{t("Submitting...")}

              </> : t("Submit Report")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>;
}
